/**
 * VillaMax Location Gate - sistema completo
 *
 * Responsable de: captura de direccion, calculo de tier, persistencia,
 * sincronizacion con Shopify cart, UI del modal, badge y bloque del carrito.
 *
 * Depende de:
 *   - window.FARMALKOSTO_DRUGSTORES (inyectado por snippets/drugstores-data.liquid)
 *   - window.google.maps (inyectado por el loader en layout/theme.liquid)
 */
(function () {
  "use strict";

  // ================== CONFIG ==================
  var GOOGLE_API_KEY = "PEGAR_API_KEY_AQUI";
  var STORAGE_KEY = "farmalkosto_location";
  var STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var MIN_ORDER_TIER_1 = 15000;
  var DEFAULT_CENTER = { lat: 4.7110, lng: -74.0721 };

  var TIER_INFO = {
    1: { name: "Entrega local gratis", detail: "30-90 min desde tu droguera mas cercana", price: 0 },
    2: { name: "Entrega Bogota",        detail: "3-5 horas - $10.000",                     price: 10000 },
    3: { name: "Entrega local Madrid",  detail: "Entrega en el dia - $5.000",              price: 5000 },
    4: { name: "Envio Nacional",        detail: "2-3 dias habiles - $13.400",              price: 13400 }
  };

  // ================== GEOMETRY ==================
  function haversineKm(lat1, lng1, lat2, lng2) {
    var R = 6371;
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.pow(Math.sin(dLat / 2), 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.pow(Math.sin(dLng / 2), 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // ================== TIER DETERMINATION ==================
  function isBogotaProvince(province) {
    if (!province) return false;
    var p = province.toLowerCase();
    return p === "bogota d.c." || p === "bogot\u00e1 d.c." ||
           p === "bogota" || p === "bogot\u00e1" ||
           p === "distrito capital" || p === "bogota, d.c." ||
           p === "bogot\u00e1, d.c.";
  }

  function isCundinamarcaProvince(province) {
    if (!province) return false;
    return province.toLowerCase() === "cundinamarca";
  }

  /**
   * Determina tier respetando fronteras de provincia (igual que Shopify).
   * - Tier 1 requiere: dentro del radio de droguer\u00eda Bogot\u00e1 Y provincia Bogot\u00e1 D.C.
   * - Tier 3 requiere: dentro del radio de droguer\u00eda Madrid Y provincia Cundinamarca.
   */
  function determineTier(lat, lng, province, drugstores) {
    var results = drugstores.map(function (d) {
      return {
        id: d.id,
        nombre: d.nombre,
        lat: d.lat,
        lng: d.lng,
        radio_km: d.radio_km,
        distance_km: haversineKm(lat, lng, d.lat, d.lng)
      };
    });

    var inBogota = isBogotaProvince(province);
    var inCundinamarca = isCundinamarcaProvince(province);

    // 1. Dentro de radio droguer\u00eda Bogot\u00e1 + provincia Bogot\u00e1 D.C.
    if (inBogota) {
      var bogotaInRadius = results
        .filter(function (d) { return d.id !== "madrid" && d.distance_km <= d.radio_km; })
        .sort(function (a, b) { return a.distance_km - b.distance_km; });
      if (bogotaInRadius.length > 0) {
        return { tier: 1, nearest_store: bogotaInRadius[0].id, distance_km: bogotaInRadius[0].distance_km };
      }
    }

    // 2. Dentro de radio droguer\u00eda Madrid + provincia Cundinamarca
    if (inCundinamarca) {
      var madrid = results.find(function (d) { return d.id === "madrid"; });
      if (madrid && madrid.distance_km <= madrid.radio_km) {
        return { tier: 3, nearest_store: "madrid", distance_km: madrid.distance_km };
      }
    }

    // 3. Provincia Bogot\u00e1 D.C. pero fuera de radios
    if (inBogota) {
      return { tier: 2, nearest_store: null, distance_km: null };
    }

    // 4. Resto de Colombia (incluye Cundinamarca fuera del radio de Madrid)
    return { tier: 4, nearest_store: null, distance_km: null };
  }

  // ================== PERSISTENCE ==================
  function saveLocation(data) {
    var payload = Object.assign({}, data, { saved_at: Date.now() });
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (e) {}
    return payload;
  }

  function loadLocation() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (Date.now() - data.saved_at > STORAGE_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) { return null; }
  }

  function clearLocation() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  // ================== CART SYNC ==================
  function syncToCart(data) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: {
          delivery_tier: String(data.tier),
          nearest_store: data.nearest_store || "",
          customer_lat: String(data.lat),
          customer_lng: String(data.lng),
          customer_address: data.address
        }
      })
    }).catch(function (e) {
      console.warn("[location-gate] cart sync failed:", e);
    });
  }

  function getCartSubtotal() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (c) { return (c.total_price || 0) / 100; })
      .catch(function () { return 0; });
  }

  // ================== GOOGLE MAPS ==================
  var _map = null, _marker = null, _autocomplete = null, _selectedPlace = null, _googleReady = false;

  function ensureGoogleReady() {
    if (_googleReady) return Promise.resolve();
    if (typeof google === "undefined" || !google.maps) {
      return Promise.reject(new Error("Google Maps no cargo. Verifica el loader en theme.liquid."));
    }
    return Promise.all([
      google.maps.importLibrary("maps"),
      google.maps.importLibrary("places"),
      google.maps.importLibrary("marker")
    ]).then(function () { _googleReady = true; });
  }

  function initMap(lat, lng) {
    var el = document.getElementById("fl-loc-map");
    if (!el) return;
    el.textContent = "";
    _map = new google.maps.Map(el, {
      center: { lat: lat, lng: lng },
      zoom: 14,
      disableDefaultUI: true,
      zoomControl: true,
      clickableIcons: false
    });
    _marker = new google.maps.Marker({
      position: { lat: lat, lng: lng },
      map: _map,
      draggable: true,
      title: "Arrastra para ajustar la ubicaci\u00f3n"
    });

    // Clic en el mapa: mueve el PIN y hace reverse geocoding
    _map.addListener("click", function (ev) {
      var pos = { lat: ev.latLng.lat(), lng: ev.latLng.lng() };
      _marker.setPosition(pos);
      reverseGeocodeAndUpdate(pos.lat, pos.lng);
    });

    // Arrastre del PIN: reverse geocoding al soltar
    _marker.addListener("dragend", function (ev) {
      var pos = { lat: ev.latLng.lat(), lng: ev.latLng.lng() };
      reverseGeocodeAndUpdate(pos.lat, pos.lng);
    });
  }

  function updateMap(lat, lng) {
    if (!_map) { initMap(lat, lng); return; }
    var pos = { lat: lat, lng: lng };
    _map.setCenter(pos);
    _map.setZoom(15);
    _marker.setPosition(pos);
  }

  function reverseGeocodeAndUpdate(lat, lng) {
    if (!_googleReady) return;
    var geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat: lat, lng: lng } },
      function (results, status) {
        if (status !== "OK" || !results || !results.length) {
          console.warn("[location-gate] reverse geocode failed:", status);
          return;
        }
        var place = results[0];
        var input = document.querySelector("[data-fl-loc-address]");
        if (input) input.value = place.formatted_address;
        _selectedPlace = {
          geometry: {
            location: {
              lat: function () { return lat; },
              lng: function () { return lng; }
            }
          },
          formatted_address: place.formatted_address,
          address_components: place.address_components
        };
        var btn = document.querySelector("[data-fl-loc-confirm]");
        if (btn) btn.disabled = false;
      }
    );
  }

  /**
   * Autocomplete migrado a Places API (New):
   * - Usa AutocompleteSuggestion.fetchAutocompleteSuggestions (reemplazo de
   *   AutocompleteService, deprecado para clientes nuevos desde 2025-03-01).
   * - Usa place.fetchFields({ fields }) para obtener detalles (reemplazo de
   *   Autocomplete widget.getPlace()).
   * - Mantiene el mismo <input data-fl-loc-address> y shape normalizado al
   *   resto del codigo (formatted_address, geometry.location.lat()/lng(),
   *   address_components[i].long_name/short_name/types).
   */
  function initAutocomplete(onPlaceSelected) {
    var input = document.querySelector("[data-fl-loc-address]");
    if (!input || _autocomplete) return;

    var Places = google.maps.places;
    if (!Places || !Places.AutocompleteSuggestion) {
      console.error("[location-gate] Places API (New) no disponible. Verifica la librería 'places' en el loader.");
      return;
    }

    var sessionToken = new Places.AutocompleteSessionToken();

    // Contenedor del dropdown (debajo del input)
    var wrapper = input.parentElement;
    if (wrapper && getComputedStyle(wrapper).position === "static") {
      wrapper.style.position = "relative";
    }
    var dropdown = document.createElement("ul");
    dropdown.className = "fl-loc-suggestions";
    dropdown.setAttribute("role", "listbox");
    dropdown.style.display = "none";
    wrapper.appendChild(dropdown);

    var debounceTimer = null;

    input.addEventListener("input", function () {
      clearTimeout(debounceTimer);
      var value = input.value.trim();
      if (value.length < 3) {
        dropdown.innerHTML = "";
        dropdown.style.display = "none";
        return;
      }
      debounceTimer = setTimeout(function () { fetchSuggestions(value); }, 200);
    });

    // Cerrar dropdown al click fuera
    document.addEventListener("click", function (e) {
      if (wrapper && !wrapper.contains(e.target)) {
        dropdown.style.display = "none";
      }
    });

    // Escape cierra el dropdown sin cerrar el modal
    input.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && dropdown.style.display !== "none") {
        e.stopPropagation();
        dropdown.style.display = "none";
      }
    });

    function fetchSuggestions(value) {
      Places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: value,
        includedRegionCodes: ["co"],
        sessionToken: sessionToken
      }).then(function (result) {
        renderSuggestions(result.suggestions || []);
      }).catch(function (err) {
        console.error("[location-gate] autocomplete error:", err);
      });
    }

    function renderSuggestions(items) {
      dropdown.innerHTML = "";
      if (!items.length) { dropdown.style.display = "none"; return; }

      items.forEach(function (s) {
        var prediction = s.placePrediction;
        if (!prediction) return;

        var li = document.createElement("li");
        li.className = "fl-loc-suggestion-item";
        li.setAttribute("role", "option");
        li.textContent = prediction.text ? prediction.text.toString() : "";
        li.addEventListener("click", function () { selectPrediction(prediction); });
        dropdown.appendChild(li);
      });
      dropdown.style.display = "block";
    }

    function selectPrediction(prediction) {
      var place = prediction.toPlace();
      place.fetchFields({
        fields: ["formattedAddress", "location", "addressComponents"]
      }).then(function () {
        var lat = place.location.lat();
        var lng = place.location.lng();

        // Shape normalizado (igual al del API legacy) para no romper el resto
        var normalized = {
          formatted_address: place.formattedAddress,
          geometry: {
            location: {
              lat: function () { return lat; },
              lng: function () { return lng; }
            }
          },
          address_components: (place.addressComponents || []).map(function (c) {
            return {
              long_name: c.longText,
              short_name: c.shortText,
              types: c.types
            };
          })
        };

        input.value = place.formattedAddress;
        dropdown.innerHTML = "";
        dropdown.style.display = "none";

        // Nuevo session token tras cada seleccion (best practice de Google)
        sessionToken = new Places.AutocompleteSessionToken();

        _selectedPlace = normalized;
        onPlaceSelected(normalized);
      }).catch(function (err) {
        console.error("[location-gate] fetchFields failed:", err);
      });
    }

    _autocomplete = true; // flag para no re-inicializar
  }

  function extractProvince(place) {
    if (!place.address_components) return null;
    var adminArea = place.address_components.find(function (c) {
      return c.types.indexOf("administrative_area_level_1") !== -1;
    });
    return adminArea ? adminArea.long_name : null;
  }

  function parseAddressComponents(place) {
    var out = {
      street_number: "",
      route: "",
      sublocality: "",
      city: "",
      province_long: "",
      province_short: "",
      postal_code: "",
      country_long: "",
      country_short: ""
    };
    if (!place.address_components) return out;
    place.address_components.forEach(function (c) {
      var t = c.types;
      if (t.indexOf("street_number") !== -1) out.street_number = c.long_name;
      if (t.indexOf("route") !== -1) out.route = c.long_name;
      if (t.indexOf("sublocality_level_1") !== -1 || t.indexOf("sublocality") !== -1) out.sublocality = c.long_name;
      if (t.indexOf("locality") !== -1) out.city = c.long_name;
      if (t.indexOf("administrative_area_level_1") !== -1) {
        out.province_long = c.long_name;
        out.province_short = c.short_name;
      }
      if (t.indexOf("postal_code") !== -1) out.postal_code = c.long_name;
      if (t.indexOf("country") !== -1) {
        out.country_long = c.long_name;
        out.country_short = c.short_name;
      }
    });
    // City fallback: si no hay locality, usar sublocality (comun en Bogota por UPZ)
    if (!out.city && out.sublocality) out.city = out.sublocality;
    return out;
  }

  function buildAddressLine(components) {
    // "Carrera 45 187-10" a partir de route + street_number
    var parts = [];
    if (components.route) parts.push(components.route);
    if (components.street_number) parts.push(components.street_number);
    return parts.join(" ").trim();
  }

  function buildCheckoutUrl(saved) {
    if (!saved) return "/checkout";
    var addressLine = buildAddressLine(saved) || saved.address;
    var params = [];
    function add(k, v) { if (v) params.push(encodeURIComponent(k) + "=" + encodeURIComponent(v)); }

    add("checkout[shipping_address][address1]", addressLine);
    add("checkout[shipping_address][city]", saved.city || "");
    add("checkout[shipping_address][province]", saved.province_long || saved.province || "");
    add("checkout[shipping_address][zip]", saved.postal_code || "");
    add("checkout[shipping_address][country]", saved.country_long || "Colombia");

    return "/checkout?" + params.join("&");
  }

  // ================== UI - MODAL ==================
  function openModal() {
    var overlay = document.getElementById("fl-loc-modal-overlay");
    if (!overlay) return;
    overlay.classList.add("fl-loc-open");
    document.body.style.overflow = "hidden";

    ensureGoogleReady().then(function () {
      var saved = loadLocation();
      var center = saved ? { lat: saved.lat, lng: saved.lng } : DEFAULT_CENTER;
      initMap(center.lat, center.lng);
      initAutocomplete(function (place) {
        var loc = place.geometry.location;
        updateMap(loc.lat(), loc.lng());
        var btn = document.querySelector("[data-fl-loc-confirm]");
        if (btn) btn.disabled = false;
      });
      if (saved) {
        var input = document.querySelector("[data-fl-loc-address]");
        if (input) input.value = saved.address;
      }
    }).catch(function (e) {
      console.error("[location-gate]", e);
    });
  }

  function closeModal() {
    var overlay = document.getElementById("fl-loc-modal-overlay");
    if (overlay) overlay.classList.remove("fl-loc-open");
    document.body.style.overflow = "";
  }

  function handleGeolocate() {
    if (!navigator.geolocation) {
      alert("Tu navegador no soporta geolocalizacion.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        var latitude = pos.coords.latitude;
        var longitude = pos.coords.longitude;
        ensureGoogleReady().then(function () {
          updateMap(latitude, longitude);
          reverseGeocodeAndUpdate(latitude, longitude);
        }).catch(function (e) {
          console.warn("[location-gate] Google not ready:", e);
        });
      },
      function (err) { alert("No se pudo obtener tu ubicacion: " + err.message); }
    );
  }

  function handleConfirm() {
    if (!_selectedPlace) return;
    var loc = _selectedPlace.geometry.location;
    var lat = typeof loc.lat === "function" ? loc.lat() : loc.lat;
    var lng = typeof loc.lng === "function" ? loc.lng() : loc.lng;
    var components = parseAddressComponents(_selectedPlace);
    var province = components.province_long || extractProvince(_selectedPlace);
    var tierResult = determineTier(lat, lng, province, window.FARMALKOSTO_DRUGSTORES || []);
    var data = Object.assign({
      address: _selectedPlace.formatted_address,
      lat: lat,
      lng: lng,
      province: province
    }, components, tierResult);
    var saved = saveLocation(data);
    syncToCart(saved).then(function () {
      updateBadge(saved);
      closeModal();
      window.dispatchEvent(new CustomEvent("farmalkosto:location-changed", { detail: saved }));
    });
  }

  // ================== UI - BADGE ==================
  function updateBadge(data) {
    var label = document.querySelector("[data-fl-loc-badge-label]");
    var value = document.querySelector("[data-fl-loc-badge-value]");
    if (!label || !value) return;
    if (data) {
      label.textContent = "Tu pedido sera enviado a:";
      value.textContent = data.address;
    } else {
      label.textContent = "Direccion de entrega";
      value.textContent = "Ingresa tu ubicacion";
    }
  }

  // ================== CART BLOCK ==================
  function findCartContainer() {
    var candidates = [
      "cart-drawer .drawer__inner",
      ".cart-drawer .drawer__inner",
      "[data-cart-drawer]",
      ".cart__footer",
      "form[action*='/cart']"
    ];
    for (var i = 0; i < candidates.length; i++) {
      var el = document.querySelector(candidates[i]);
      if (el) return el;
    }
    return null;
  }

  function findCheckoutButton() {
    var candidates = [
      "button[name='checkout']",
      "input[name='checkout']",
      ".cart__checkout-button",
      "[data-cart-checkout]"
    ];
    for (var i = 0; i < candidates.length; i++) {
      var el = document.querySelector(candidates[i]);
      if (el) return el;
    }
    return null;
  }

  function renderCartLocationBlock() {
    // Si el banner nuevo (#vx-cart-banner) ya existe, no dibujamos el bloque legacy
    if (document.getElementById("vx-cart-banner")) return;
    var container = findCartContainer();
    if (!container) return;

    var block = container.querySelector("[data-fl-loc-cart-block]");
    if (!block) {
      container.insertAdjacentHTML("afterbegin", '<div class="fl-loc-cart-block" data-fl-loc-cart-block></div>');
      block = container.querySelector("[data-fl-loc-cart-block]");
    }

    var saved = loadLocation();
    var checkoutBtn = findCheckoutButton();

    if (!saved) {
      block.classList.add("fl-loc-cart-block--no-location");
      block.innerHTML =
        '<div class="fl-loc-cart-block__tier">Define tu ubicacion</div>' +
        '<div class="fl-loc-cart-block__detail">Para ver el costo de envio antes de pagar.</div>' +
        '<button type="button" class="fl-loc-cart-block__link" data-fl-loc-open>Definir ubicacion</button>';
      if (checkoutBtn) { checkoutBtn.disabled = false; checkoutBtn.title = ""; }
      return;
    }

    block.classList.remove("fl-loc-cart-block--no-location");
    var info = TIER_INFO[saved.tier] || TIER_INFO[4];

    getCartSubtotal().then(function (subtotal) {
      var warningHtml = "";
      var disableCheckout = false;

      if (saved.tier === 1 && subtotal < MIN_ORDER_TIER_1) {
        var falta = MIN_ORDER_TIER_1 - subtotal;
        warningHtml =
          '<div class="fl-loc-cart-block__warning">Agrega $' +
          falta.toLocaleString("es-CO") +
          ' mas para envio gratis (minimo $' +
          MIN_ORDER_TIER_1.toLocaleString("es-CO") +
          ').</div>';
        disableCheckout = true;
      }

      block.innerHTML =
        '<div class="fl-loc-cart-block__tier">' + info.name + '</div>' +
        '<div class="fl-loc-cart-block__detail">' + info.detail + '</div>' +
        '<div class="fl-loc-cart-block__detail">' + saved.address + '</div>' +
        warningHtml +
        '<button type="button" class="fl-loc-cart-block__link" data-fl-loc-open>Cambiar direccion</button>';

      if (checkoutBtn) {
        checkoutBtn.disabled = disableCheckout;
        checkoutBtn.title = disableCheckout
          ? "Agrega mas productos (minimo $" + MIN_ORDER_TIER_1.toLocaleString("es-CO") + " para envio gratis)"
          : "";
      }
    });
  }

  // ================== CHECKOUT REDIRECT ==================
  function interceptCheckoutClick(e) {
    var btn = e.target.closest(
      "button[name='checkout'], input[name='checkout'], .cart__checkout-button, [data-cart-checkout]"
    );
    if (!btn) return false;
    var saved = loadLocation();
    if (!saved || !saved.address) return false; // sin direccion, flujo normal

    e.preventDefault();
    e.stopImmediatePropagation();
    window.location.href = buildCheckoutUrl(saved);
    return true;
  }

  // ================== BOOTSTRAP ==================
  function bindEvents() {
    // Checkout interceptor tiene que correr ANTES del handler generico, con capture:true
    document.addEventListener("click", interceptCheckoutClick, true);

    document.addEventListener("click", function (e) {
      if (e.target.closest("[data-fl-loc-open]")) { openModal(); return; }
      if (e.target.closest("[data-fl-loc-close]")) { closeModal(); return; }
      if (e.target.closest("[data-fl-loc-geolocate]")) { handleGeolocate(); return; }
      if (e.target.closest("[data-fl-loc-confirm]")) { handleConfirm(); return; }
      if (e.target.id === "fl-loc-modal-overlay") { closeModal(); return; }
      if (e.target.closest("[data-cart-open], .cart-icon, a[href='/cart'], a[href$='/cart']")) {
        setTimeout(function () { renderCartLocationBlock(); }, 300);
      }
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeModal();
    });

    window.addEventListener("farmalkosto:location-changed", function () {
      renderCartLocationBlock();
    });
    document.addEventListener("cart:updated", function () {
      renderCartLocationBlock();
    });
  }

  function init() {
    bindEvents();

    var saved = loadLocation();
    if (saved) {
      updateBadge(saved);
    } else {
      updateBadge(null);
      setTimeout(function () { openModal(); }, 1200);
    }

    if (window.location.pathname === "/cart" || window.location.pathname.indexOf("/cart") === 0) {
      setTimeout(function () { renderCartLocationBlock(); }, 400);
    }
  }

  // Export para debugging
  window.FarmalkostoLocation = {
    haversineKm: haversineKm,
    determineTier: determineTier,
    saveLocation: saveLocation,
    loadLocation: loadLocation,
    clearLocation: clearLocation,
    syncToCart: syncToCart,
    openModal: openModal,
    closeModal: closeModal,
    renderCartLocationBlock: renderCartLocationBlock,
    buildCheckoutUrl: buildCheckoutUrl
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();